import { InputBaseModel, InputModelWithSource } from '../models';
import { SourceFetchConditionFieldDto, SourceFetchConditionDto, SourceFetchConditionOperator } from '../types';
import { InstanceUtils } from '.';

type FieldValidationResult = {
    fieldId: string;
    isValid: boolean;
};

type ValidationResult = {
    shouldFetch: boolean;
    inputs: InputBaseModel[];
};

export default class SourceFetchConditionValidator {
    public static shouldFetchSource(condition: SourceFetchConditionDto, inputs: InputBaseModel[]): boolean {
        const fieldResults = SourceFetchConditionValidator.validateFields(condition.fields, inputs);
        return SourceFetchConditionValidator.validateCondition(condition.conditionOperator, fieldResults);
    }

    public static getRelevantInputs(input: InputBaseModel): InputBaseModel[] {
        if (InstanceUtils.isBalanceInformationNode(input.node)) {
            return [...input.node.inputs, ...input.node.childNodes.flatMap(c => c.inputs)];
        }

        if (InstanceUtils.isEntryCodeNode(input.node)) {
            return [
                ...(input.node.worksheet.generalInformationNode?.inputs ?? []),
                ...(input.node.worksheet.balanceInformationNodes.find(n =>
                    n.childNodes.map(c => c.id).includes(input.node.id)
                )?.inputs ?? [])
            ];
        }

        return [];
    }

    public static getDependentInputs(input: InputBaseModel): InputModelWithSource[] {
        const allRelevantInputs = SourceFetchConditionValidator.getRelevantInputs(input);

        return allRelevantInputs.filter((i): i is InputModelWithSource => {
            if (!InstanceUtils.isInputWithSource(i) || !i.sourceFetchCondition) {
                return false;
            }

            // Check if this input's sourceFetchCondition depends on the current input
            return i.sourceFetchCondition.fields.some(f => f.fieldId === input.id);
        });
    }

    public static validateSourceFetchCondition(
        input: InputBaseModel,
        condition: SourceFetchConditionDto
    ): ValidationResult {
        const inputIds = condition.fields.map(f => f.fieldId);
        const allInputs = SourceFetchConditionValidator.getRelevantInputs(input);
        const inputs = allInputs.filter(i => inputIds.includes(i.id));

        const shouldFetch = SourceFetchConditionValidator.shouldFetchSource(condition, inputs);

        return {
            shouldFetch,
            inputs
        };
    }

    private static validateCondition(
        conditionOperator: SourceFetchConditionOperator,
        fieldResults: FieldValidationResult[]
    ): boolean {
        switch (conditionOperator) {
            case 'And':
                return fieldResults.every(r => r.isValid);
            case 'Or':
                return fieldResults.some(r => r.isValid);
            default:
                return false;
        }
    }

    private static validateFields(
        fields: SourceFetchConditionFieldDto[],
        inputs: InputBaseModel[]
    ): FieldValidationResult[] {
        return fields.map(field => {
            const fieldInput = inputs.find(i => i.id === field.fieldId);
            const isValid = fieldInput ? SourceFetchConditionValidator.validateField(field, fieldInput) : false;

            return {
                fieldId: field.fieldId,
                isValid
            };
        });
    }

    private static validateField(field: SourceFetchConditionFieldDto, input: InputBaseModel): boolean {
        switch (field.fieldOperator) {
            case 'Equal':
                return input.value === field.fieldValue;
            case 'NotEqual':
                return input.value !== field.fieldValue;
            case 'HasValue':
                return input.hasValue;
            case 'NotEmpty':
                return input.hasValue && input.value !== '' && input.value !== null && input.value !== undefined;
            default:
                return false;
        }
    }
}
